import { Component, effect, OnDestroy, OnInit, EffectRef } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnboardingProductsStore } from '../../../store/store';
import {
  PasswordCreateActions,
  UiActions
} from '../../../store/actions';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { LoadingService } from '../../../services/loading.service';
import { ApiResponseStatus, StepsNotClientTypes } from '../../../shared/enums';
import { environment } from 'src/environments/environment';
import { DatadogService } from 'src/app/services/datadog/datadog.service';
import { TagPage } from 'src/app/store/actions/ui.actions';
import { DataNotClientService } from 'src/app/services/data-not-client.service';
import { RequestPasswordCreate } from 'src/app/shared/interfaces';
import { SignalService } from 'src/app/services/signals/signal.service';

@Component({
  selector: 'app-create-password',
  templateUrl: './create-password.component.html',
  styleUrls: ['../../../global.scss', './create-password.component.scss']
})
export class CreatePasswordComponent implements OnInit, OnDestroy {
  maxCountRetry = 3;
  countRetry = 0;
  fadeout: boolean;
  serviceError: boolean;
  requestSend: boolean;
  displayTooltip: boolean;
  formData: FormGroup;
  errorMiddleware;
  errorEffect: EffectRef;

  staticAssetsUrl = environment.staticScripts.staticAssets;

  passwordCreate$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.passwordCreate)
  );


  constructor(
    public loadingService: LoadingService,
    private _fb: FormBuilder,
    private signalService: SignalService,
    private dataDogService: DatadogService,
    private dataNotClientService: DataNotClientService,
    private store: Store<OnboardingProductsStore.AppState>) {
      this.formData = this._fb.group({
        firstClientPasswordInput: ['', [Validators.required, Validators.pattern('^[0-9]*$'), this.preventSixInRow, this.preventFourEqualInRow]],
        secondClientPasswordInput: ['', [Validators.required, Validators.pattern('^[0-9]*$'), this.preventSixInRow, this.comparePasswordInputs, this.preventFourEqualInRow]]
      });
      this.errorMiddleware = this.signalService.createPasswordError;
      this.errorEffect = effect(() => {
        if (this.errorMiddleware()) {
          this.continueWithoutCreation();
        }
      }, { allowSignalWrites: true });
   }

   passwordCreateSuscription = this.passwordCreate$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }
    if (error != null) {
      this.dataDogService.addCustomError(error, 'Error on password create', environment.mode, { channel: 'no client' });
      this.handleErrorResponse();
    }
    if (response) {
      if (response.client.operationState.code === ApiResponseStatus.OK) {
        this.handleSuccessResponse();
      } else {
        this.dataDogService.addCustomEvent(`Fail on password create`, environment.mode, { channel: 'no client' });
        this.handleErrorResponse();
      }
    }
  });

  ngOnInit() {
    this.store.dispatch(TagPage({text: 'create-password'}));
    this.handleWhiteContainer('block');
    setTimeout(() => {
      this.loadingService.loading = false;
    }, 200);
  }

  ngOnDestroy() {
    this.passwordCreateSuscription.unsubscribe();
    this.errorEffect?.destroy();
  }

  // Metodo para ocultar o mostrar el contenedor blanco - recordar solo usar 'none' o 'block'
  handleWhiteContainer(canShow: string) {
    const inputElement = document.getElementById('white-container');
    inputElement.style.display = canShow;
  }

  continue() {
    if (this.requestSend) {
      return;
    }
    this.formData.disable();
    this.requestSend = true;
    this.loadingService.loading = true;
    const request: RequestPasswordCreate = {
      client: {
        password: this.formData.controls.secondClientPasswordInput.value
      }
    };
    this.store.dispatch(PasswordCreateActions.getPasswordCreate({ request }));
  }

  continueWithoutCreation() {
    this.formData.disable();
    this.requestSend = true;
    this.loadingService.loading = true;
    this.dataNotClientService.setPasswordState(false);
    return this.store.dispatch(UiActions.Navigate({ step: StepsNotClientTypes.SUCCESS_PROCESS }));
  }

  handleErrorResponse() {
    this.countRetry++;
    this.loadingService.loading = false;
    this.requestSend = false;
    this.serviceError = true;
    this.formData.reset();
    this.formData.enable();
    if (this.countRetry >= this.maxCountRetry) {
      this.formData.disable();
      this.serviceError = false;
    }
  }

  handleSuccessResponse() {
    this.dataNotClientService.setPasswordState(true);
    this.dataDogService.addCustomEvent('Password Created', environment.mode, { channel: 'no client' });
    this.store.dispatch(UiActions.Navigate({ step: StepsNotClientTypes.SUCCESS_PROCESS }));
  }

  toggleTooltip() {
    setTimeout(() => {
      this.displayTooltip = true;
    }, 200);
  }

  hideTooltip() {
    if (this.formData.controls.firstClientPasswordInput && this.formData.controls.secondClientPasswordInput &&
      this.formData.controls.firstClientPasswordInput.value === this.formData.controls.secondClientPasswordInput.value) {
      setTimeout(() => {
        this.fadeout = true;
        setTimeout(() => {
          this.displayTooltip = false;
          this.fadeout = false;
        }, 200);
      }, 100);
    } else {
      this.toggleTooltip();
    }
  }

  preventSixInRow(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.length === 6) {
      let countInRow = 0;
      if (Number(control.value[0]) === 0) {
        countInRow++;
      }
      for (let i = 0; i < control.value.length; i++) {
        const current = Number(control.value[i]);
        const next = Number(control.value[i + 1]);
        if (Math.abs(current - next) === 1) {
          countInRow++;
          if (countInRow === 5) {
            return { sixInRow: true };
          }
        }
      }
      return null;
    }
    return null;
  }

  preventFourEqualInRow(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.length >= 4) {
      let countEqualNumber = 1;
      for (let i = 0; i < control.value.length; i++) {
        const current = Number(control.value[i]);
        const next = Number(control.value[i + 1]);
        if (current === next) {
          countEqualNumber++;
          if (countEqualNumber === 4) {
            return { fourEqualInRow: true };
          }
        }
      }
    }
  }

  comparePasswordInputs(control: AbstractControl): ValidationErrors | null {
    // Esta linea hace referencia al formulario, que me permite acceder a otros campos.
    const parentForm = control.parent;
    // Prevengo que se acceda al parentForm antes de ser utilizado.
    if (!parentForm) {
      return null;
    }

    const firstInputPassword = parentForm.get('firstClientPasswordInput').value;

    if (firstInputPassword && control.value && control.value.length === 6) {
      if (firstInputPassword !== control.value) {
        return { notEqualPassword: true };
      }
      return null;
    }
    return null;
  }
}

